<script setup lang="ts">
import KgrIcon from '@shared/components/KgrIcon.vue';
import KgrProfileImage from '@shared/components/KgrProfileImage.vue';
import { usePageBack } from '@shared/composables/usePageBack';
import { useUserStore } from '@shared/stores/user';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type MenuState = 'idle' | 'mainMenuOpen' | 'userMenuOpen';

defineProps<{ menuState: MenuState }>();
// Toggle semantics like Angular `onMainMenuToggleClick`/`onUserMenuToggleClick`: clicking the
// same toggle again closes the menu. App.vue owns the state and decides.
const emit = defineEmits<{
    'toggle-main-menu': []
    'toggle-user-menu': []
}>();

const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const { backLink } = usePageBack();

// Angular sets `is-inverted` from `route.data.invertedHeader` (child/profile page); in Vue it
// comes from the route's `route.meta.invertedHeader`.
const inverted = computed(() => !!route.meta.invertedHeader);

const profileImageSrc = computed(() => {
    const user = userStore.currentUser;
    if (!user?.hasProfileImage) {
        return null;
    }
    return `${window.kgrDynamicConfig.apiHost}${user.profileImageApiUrl}`;
});
</script>

<template>
    <div
        class="kgr-pageHeader kgr-pageHeader--fullWidth"
        :class="{ 'is-inverted': inverted }"
    >
        <div class="kgr-pageHeader__inner">
            <div class="kgr-pageHeader__item">
                <button
                    class="kgr-pageHeader__item"
                    type="button"
                    @click.prevent.stop="emit('toggle-main-menu')"
                >
                    <div class="menuToggle kgr-menuToggle" :class="[{ 'is-open': menuState === 'mainMenuOpen' }]">
                        <span class="kgr-menuToggle__line"></span>
                        <span class="kgr-menuToggle__line"></span>
                        <span class="kgr-menuToggle__line"></span>
                    </div>
                </button>
            </div>
            <div
                v-if="backLink"
                class="kgr-pageHeader__item"
            >
                <button
                    type="button"
                    tabindex="0"
                    class="kgr-clickable kgr-clickable--touchy"
                    @click="router.push(backLink)"
                >
                    <KgrIcon
                        name="arrow-back"
                        class="kgr-postfix__icon"
                    />
                </button>
            </div>
            <div
                id="kgr-pageTitle"
                class="kgr-pageHeader__item kgr-pageHeader__item--grow title"
            ></div>
            <button
                class="kgr-pageHeader__item kgr-clickable kgr-clickable--touchy"
                type="button"
                @click.prevent.stop="emit('toggle-user-menu')"
            >
                <KgrProfileImage
                    :src="profileImageSrc"
                    size="s"
                />
            </button>
        </div>
    </div>
</template>

<style scoped>
.title {
    justify-content: center;
    font-size: var(--kgr-font-size-text-big);
    font-weight: var(--kgr-font-weight-bold);
}
</style>
